import React, { useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { ChevronLeft, ChevronRight, Link2 } from 'lucide-react';
import type { Meeting } from '../../types';
import type { CalendarEventDTO } from '../../global';
import { useCalendar } from '../calendar/useCalendar';
import { useAutoJoin } from '../calendar/useAutoJoin';
import { cn } from '../../components/ui/cn';
import { dateLocale, t } from '../../lib/i18n';
import { MobileCard, MobileSection, MobileSegmented, MobileSwitch, MobileTitle } from './MobileUI';

type CalendarMode = 'day' | 'week' | 'month';

interface MobileCalendarViewProps {
  meetings: Meeting[];
  onSelectMeeting: (meeting: Meeting) => void;
  onError: (message: string) => void;
}

// — Tarih yardımcıları (yerel saat) —

const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
const addDays = (date: Date, days: number) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
const dayKey = (date: Date) => `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
/** Haftanın ilk günü pazartesi. */
const mondayOf = (date: Date) => addDays(startOfDay(date), -((date.getDay() + 6) % 7));
const fmt = (date: Date, options: Intl.DateTimeFormatOptions) => date.toLocaleDateString(dateLocale(), options);
const clock = (date: Date) => date.toLocaleTimeString(dateLocale(), { hour: '2-digit', minute: '2-digit' });

interface DayCounts {
  past: number;
  future: number;
}

/** Bu kadar yatay kayınca sürükleme başlar; daha azı dokunuş sayılır. */
const DRAG_START_PX = 8;
const SLIDE_MS = 160;

/**
 * Hafta şeridi ve ay ızgarası parmakla kaydırılır: içerik parmağı izler,
 * yeterince kayınca önceki/sonraki aralığa geçer ve yeni aralık öbür yandan
 * kayarak gelir. Dikey kaydırma tarayıcıda kalıyor (touch-action: pan-y).
 * Sürükleme sırasında her harekette yeniden çizim olmasın diye kaydırma
 * stili doğrudan öğeye yazılıyor.
 */
function useSwipe(onSwipe: (direction: 1 | -1) => void) {
  const onSwipeRef = useRef(onSwipe);
  onSwipeRef.current = onSwipe;
  const trackRef = useRef<HTMLDivElement>(null);
  const start = useRef<{ x: number; y: number; id: number } | null>(null);
  const dragging = useRef(false);
  const suppressClick = useRef(false);

  const move = (x: number, animate: boolean) => {
    const el = trackRef.current;
    if (!el) return;
    el.style.transition = animate ? `transform ${SLIDE_MS}ms ease-out` : 'none';
    el.style.transform = x ? `translateX(${x}px)` : '';
  };

  const release = (event: React.PointerEvent<HTMLDivElement>, cancelled: boolean) => {
    const origin = start.current;
    if (!origin || origin.id !== event.pointerId) return;
    start.current = null;
    if (!dragging.current) return;
    dragging.current = false;
    suppressClick.current = true;

    const dx = event.clientX - origin.x;
    const width = event.currentTarget.clientWidth;
    if (cancelled || Math.abs(dx) < Math.min(60, width / 5)) {
      move(0, true);
      return;
    }
    // Sola kaydırmak sonrakine, sağa kaydırmak öncekine götürür.
    const direction = dx < 0 ? 1 : -1;
    move(-direction * width, true);
    window.setTimeout(() => {
      flushSync(() => onSwipeRef.current(direction));
      move(direction * width, false);
      void trackRef.current?.offsetWidth; // başlangıç konumu çizilsin, sonra kaysın
      move(0, true);
    }, SLIDE_MS);
  };

  return {
    trackRef,
    handlers: {
      onPointerDown: (event: React.PointerEvent<HTMLDivElement>) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        start.current = { x: event.clientX, y: event.clientY, id: event.pointerId };
        dragging.current = false;
        suppressClick.current = false;
      },
      onPointerMove: (event: React.PointerEvent<HTMLDivElement>) => {
        const origin = start.current;
        if (!origin || origin.id !== event.pointerId) return;
        const dx = event.clientX - origin.x;
        if (!dragging.current) {
          const dy = event.clientY - origin.y;
          if (Math.abs(dy) > DRAG_START_PX && Math.abs(dy) > Math.abs(dx)) {
            start.current = null; // dikey kaydırma
            return;
          }
          if (Math.abs(dx) < DRAG_START_PX) return;
          dragging.current = true;
          event.currentTarget.setPointerCapture(event.pointerId);
        }
        move(dx, false);
      },
      onPointerUp: (event: React.PointerEvent<HTMLDivElement>) => release(event, false),
      onPointerCancel: (event: React.PointerEvent<HTMLDivElement>) => release(event, true),
      // Sürüklemenin sonundaki bırakış bir güne dokunmak sayılmasın.
      onClickCapture: (event: React.MouseEvent<HTMLDivElement>) => {
        if (!suppressClick.current) return;
        suppressClick.current = false;
        event.preventDefault();
        event.stopPropagation();
      },
    },
  };
}

/**
 * Takvim sekmesi. Geçmiş = o gün kaydedilen toplantılar, gelecek = bağlı
 * takvimdeki henüz bitmemiş etkinlikler. Gün görünümünde ızgara yok; hafta
 * ve ayda ızgara, altında seçili günün listesi.
 */
export const MobileCalendarView: React.FC<MobileCalendarViewProps> = ({ meetings, onSelectMeeting, onError }) => {
  const calendar = useCalendar();
  const autoJoin = useAutoJoin();
  const [mode, setMode] = useState<CalendarMode>('day');
  const [selected, setSelected] = useState(() => startOfDay(new Date()));
  const today = startOfDay(new Date());

  // Gün → kayıtlı toplantılar / gelecek etkinlikler.
  const pastByDay = useMemo(() => {
    const map = new Map<string, Meeting[]>();
    for (const meeting of meetings) {
      const at = new Date(meeting.recordedAtISO);
      if (Number.isNaN(at.getTime())) continue;
      const key = dayKey(at);
      map.set(key, [...(map.get(key) ?? []), meeting]);
    }
    return map;
  }, [meetings]);

  const futureByDay = useMemo(() => {
    const now = Date.now();
    const map = new Map<string, CalendarEventDTO[]>();
    for (const event of calendar.events) {
      if (new Date(event.endISO).getTime() <= now) continue;
      const key = dayKey(new Date(event.startISO));
      map.set(key, [...(map.get(key) ?? []), event]);
    }
    return map;
  }, [calendar.events]);

  const countsFor = (date: Date): DayCounts => ({
    past: pastByDay.get(dayKey(date))?.length ?? 0,
    future: futureByDay.get(dayKey(date))?.length ?? 0,
  });

  // Seçili aralık: gün, pazartesiden başlayan hafta ya da ay.
  const range = useMemo(() => {
    if (mode === 'day') return { start: selected, days: 1 };
    if (mode === 'week') return { start: mondayOf(selected), days: 7 };
    const first = new Date(selected.getFullYear(), selected.getMonth(), 1);
    const days = new Date(selected.getFullYear(), selected.getMonth() + 1, 0).getDate();
    return { start: first, days };
  }, [mode, selected]);

  const totals = useMemo(() => {
    let past = 0;
    let future = 0;
    for (let i = 0; i < range.days; i++) {
      const counts = countsFor(addDays(range.start, i));
      past += counts.past;
      future += counts.future;
    }
    return { past, future, total: past + future };
    // countsFor yalnızca bu iki haritaya bağlı.
  }, [range, pastByDay, futureByDay]);

  const rangeLabel = (() => {
    if (mode === 'day') return fmt(selected, { day: 'numeric', month: 'long', weekday: 'long' });
    if (mode === 'month') return fmt(selected, { month: 'long', year: 'numeric' });
    const end = addDays(range.start, 6);
    return range.start.getMonth() === end.getMonth()
      ? `${range.start.getDate()} – ${fmt(end, { day: 'numeric', month: 'long' })}`
      : `${fmt(range.start, { day: 'numeric', month: 'short' })} – ${fmt(end, { day: 'numeric', month: 'short' })}`;
  })();

  const step = (direction: 1 | -1) => {
    if (mode === 'day') setSelected((d) => addDays(d, direction));
    else if (mode === 'week') setSelected((d) => addDays(d, 7 * direction));
    else setSelected((d) => new Date(d.getFullYear(), d.getMonth() + direction, 1));
  };
  const swipe = useSwipe(step);

  // Seçili günün listesi: kayıtlar ve gelecek etkinlikler saat sırasıyla.
  const dayPast = [...(pastByDay.get(dayKey(selected)) ?? [])].sort((a, b) => a.recordedAtISO.localeCompare(b.recordedAtISO));
  const dayFuture = [...(futureByDay.get(dayKey(selected)) ?? [])].sort((a, b) => a.startISO.localeCompare(b.startISO));
  const isSelectedToday = selected.getTime() === today.getTime();

  const toggleBot = async (event: CalendarEventDTO, join: boolean) => {
    try {
      await autoJoin.setEventJoin(event, join);
    } catch (err) {
      onError((err as Error).message || t('Tercih kaydedilemedi.', 'Could not save your preference.'));
    }
  };

  // Yalnızca dolu noktalar çiziliyor: boş yer tutucular tek noktayı sola itiyordu.
  const dots = (counts: DayCounts, isPastDay: boolean, size: 'sm' | 'md') => {
    const n = Math.min(3, counts.past + counts.future);
    return (
      <span className={cn('flex justify-center gap-[3px]', size === 'md' ? 'h-[5px]' : 'h-1')} aria-hidden="true">
        {Array.from({ length: n }, (_, i) => (
          <span
            key={i}
            className={cn(
              'rounded-full',
              size === 'md' ? 'w-[5px] h-[5px]' : 'w-1 h-1',
              counts.future > 0 && !isPastDay ? 'bg-brand-text' : 'bg-muted-foreground'
            )}
          />
        ))}
      </span>
    );
  };

  const dayNumber = (date: Date, size: 'sm' | 'md') => {
    const isSel = date.getTime() === selected.getTime();
    const isToday = date.getTime() === today.getTime();
    return (
      <span
        className={cn(
          'rounded-full flex items-center justify-center font-bold',
          size === 'md' ? 'w-8 h-8 text-[15px]' : 'w-[30px] h-[30px] text-[14.5px] font-semibold',
          isSel ? 'bg-brand text-on-brand' : isToday ? 'bg-brand-soft text-brand-text' : 'text-foreground'
        )}
      >
        {date.getDate()}
      </span>
    );
  };

  const ariaFor = (date: Date, counts: DayCounts) =>
    `${fmt(date, { day: 'numeric', month: 'long', weekday: 'long' })}, ${t(
      `${counts.past + counts.future} toplantı`,
      `${counts.past + counts.future} meetings`
    )}`;

  // Ay ızgarası: pazartesiden başlayan haftalar, ay dışı günler boş.
  const monthCells = useMemo(() => {
    if (mode !== 'month') return [];
    const first = range.start;
    const lead = (first.getDay() + 6) % 7;
    const cells: Array<Date | null> = Array.from({ length: lead }, () => null);
    for (let i = 0; i < range.days; i++) cells.push(addDays(first, i));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [mode, range]);

  const weekdayNames = Array.from({ length: 7 }, (_, i) => fmt(addDays(mondayOf(today), i), { weekday: 'short' }));

  return (
    <div className="px-5 pb-8 space-y-4 select-none">
      <MobileTitle
        title={t('Takvim', 'Calendar')}
        trailing={
          <button
            type="button"
            onClick={() => setSelected(today)}
            className="h-10 px-4 rounded-full bg-secondary text-[14px] font-semibold text-foreground active:opacity-70"
          >
            {t('Bugün', 'Today')}
          </button>
        }
      />

      <MobileSegmented
        label={t('Takvim görünümü', 'Calendar view')}
        value={mode}
        onChange={setMode}
        options={[
          { value: 'day', label: t('Gün', 'Day') },
          { value: 'week', label: t('Hafta', 'Week') },
          { value: 'month', label: t('Ay', 'Month') },
        ]}
      />

      <div className="flex items-center justify-between">
        <button type="button" onClick={() => step(-1)} aria-label={t('Önceki', 'Previous')} className="w-11 h-11 rounded-full flex items-center justify-center text-foreground active:bg-secondary">
          <ChevronLeft className="w-6 h-6" />
        </button>
        <span className="text-[16px] font-bold text-foreground first-letter:uppercase">{rangeLabel}</span>
        <button type="button" onClick={() => step(1)} aria-label={t('Sonraki', 'Next')} className="w-11 h-11 rounded-full flex items-center justify-center text-foreground active:bg-secondary">
          <ChevronRight className="w-6 h-6" />
        </button>
      </div>

      {/* Sayaçlar: seçili aralıktaki toplantılar */}
      <div className="grid grid-cols-3 gap-2">
        <Stat value={totals.total} label={t('Toplantı', 'Meetings')} />
        <Stat value={totals.past} label={t('Geçmiş', 'Past')} />
        <Stat value={totals.future} label={t('Gelecek', 'Upcoming')} accent />
      </div>

      {mode === 'week' && (
        <MobileCard>
          <div {...swipe.handlers} className="touch-pan-y">
            <div ref={swipe.trackRef} className="grid grid-cols-7 gap-1 px-1.5 py-2">
              {Array.from({ length: 7 }, (_, i) => addDays(range.start, i)).map((date) => {
                const counts = countsFor(date);
                const isSel = date.getTime() === selected.getTime();
                return (
                  <button
                    key={dayKey(date)}
                    type="button"
                    onClick={() => setSelected(date)}
                    aria-label={ariaFor(date, counts)}
                    aria-pressed={isSel}
                    className={cn('flex flex-col items-center gap-1.5 py-2 rounded-[14px]', isSel && 'bg-secondary')}
                  >
                    <span className="text-[12px] font-semibold text-muted-foreground">{weekdayNames[(date.getDay() + 6) % 7]}</span>
                    {dayNumber(date, 'md')}
                    {dots(counts, date < today, 'md')}
                  </button>
                );
              })}
            </div>
          </div>
        </MobileCard>
      )}

      {mode === 'month' && (
        <MobileCard>
          <div {...swipe.handlers} className="touch-pan-y">
            <div ref={swipe.trackRef} className="px-1.5 py-2.5 space-y-1">
              <div className="grid grid-cols-7 text-center text-[12px] font-semibold text-muted-foreground pb-1" aria-hidden="true">
                {weekdayNames.map((name) => (
                  <span key={name}>{name}</span>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-y-0.5">
                {monthCells.map((date, index) =>
                  date ? (
                    <button
                      key={dayKey(date)}
                      type="button"
                      onClick={() => setSelected(date)}
                      aria-label={ariaFor(date, countsFor(date))}
                      aria-pressed={date.getTime() === selected.getTime()}
                      className="h-12 flex flex-col items-center justify-center gap-[3px]"
                    >
                      {dayNumber(date, 'sm')}
                      {dots(countsFor(date), date < today, 'sm')}
                    </button>
                  ) : (
                    <span key={`blank-${index}`} className="h-12" />
                  )
                )}
              </div>
            </div>
          </div>
        </MobileCard>
      )}

      {(calendar.status === 'not_connected' || calendar.status === 'auth_expired') && (
        <button
          type="button"
          onClick={() => void calendar.connect()}
          className="w-full flex items-center gap-2 text-left text-[14px] font-semibold text-brand-text py-1"
        >
          <Link2 className="w-4 h-4 shrink-0" />
          {t('Gelecek toplantıları görmek için takvimini bağla', 'Connect your calendar to see upcoming meetings')}
        </button>
      )}

      <MobileSection
        title={`${isSelectedToday ? `${t('Bugün', 'Today')} · ` : ''}${fmt(selected, { day: 'numeric', month: 'long', weekday: 'long' })}`}
      >
        {dayPast.length + dayFuture.length === 0 ? (
          <MobileCard className="py-6 px-4 text-center text-[15px] text-muted-foreground">
            {t('Bu gün toplantı yok.', 'No meetings on this day.')}
          </MobileCard>
        ) : (
          <MobileCard>
            {dayPast.map((meeting) => (
              <button
                key={meeting.id}
                type="button"
                onClick={() => onSelectMeeting(meeting)}
                className="w-full min-h-[68px] flex items-center gap-3 pl-4 pr-3.5 py-2.5 text-left border-b border-border last:border-b-0 active:bg-secondary/60"
              >
                <span className="w-12 shrink-0 text-[15px] font-bold text-muted-foreground tabular-nums">{clock(new Date(meeting.recordedAtISO))}</span>
                <span className="flex-1 min-w-0 flex flex-col gap-0.5">
                  <span className="text-[16px] leading-[21px] font-semibold text-foreground line-clamp-2 break-words">{meeting.title}</span>
                  <span className="text-[13.5px] text-muted-foreground truncate">
                    {meeting.duration}
                    {meeting.participants.length > 1 ? ` · ${t(`${meeting.participants.length} kişi`, `${meeting.participants.length} people`)}` : ''}
                  </span>
                </span>
                <ChevronRight className="w-[18px] h-[18px] shrink-0 text-muted-foreground" />
              </button>
            ))}
            {dayFuture.map((event) => {
              const state = autoJoin.stateFor(event);
              const start = new Date(event.startISO);
              const where = event.conference?.label ?? event.location;
              const botText =
                state.kind === 'toggle'
                  ? state.on
                    ? t('bot katılacak', 'bot will join')
                    : t('bot katılmayacak', 'no bot')
                  : null;
              return (
                <div key={event.id} className="min-h-[68px] flex items-center gap-3 pl-4 pr-3 py-2.5 border-b border-border last:border-b-0">
                  <span className="w-12 shrink-0 text-[15px] font-bold text-brand-text tabular-nums">
                    {event.isAllDay ? t('Tüm gün', 'All day') : clock(start)}
                  </span>
                  <span className="flex-1 min-w-0 flex flex-col gap-0.5">
                    <span className="text-[16px] leading-[21px] font-semibold text-foreground line-clamp-2 break-words">{event.title}</span>
                    <span className="text-[13.5px] text-muted-foreground line-clamp-2">
                      {[where, botText].filter(Boolean).join(' · ') || t('Gelecek', 'Upcoming')}
                    </span>
                  </span>
                  {state.kind === 'toggle' && (
                    <MobileSwitch
                      checked={state.on}
                      onChange={(join) => void toggleBot(event, join)}
                      label={t(`Bot "${event.title}" toplantısına katılsın`, `Bot joins "${event.title}"`)}
                    />
                  )}
                </div>
              );
            })}
          </MobileCard>
        )}
      </MobileSection>
    </div>
  );
};

const Stat: React.FC<{ value: number; label: string; accent?: boolean }> = ({ value, label, accent }) => (
  <div
    className={cn(
      'rounded-2xl px-3.5 py-3 flex flex-col gap-0.5',
      accent ? 'bg-brand-soft text-brand-text' : 'bg-card border border-border text-foreground'
    )}
  >
    <span className="text-[24px] leading-[30px] font-bold tabular-nums">{value}</span>
    <span className={cn('text-[13px] font-semibold', accent ? 'text-brand-text' : 'text-muted-foreground')}>{label}</span>
  </div>
);
